/**
 * R11 #649 (PRQ-08.1, plan §7 L0, ADR-022) — the L0 platform shell's entries and the Admin groups,
 * as GET /api/apps answers them from the platform declaration (api/src/modules/platform-shell.ts).
 * Nothing here names an entry: the web renders what the declaration lists, already filtered to the
 * caller's roles, and filters once more by each item's `roles` so a stale cache never shows an entry
 * the role cannot use. Hidden, never disabled.
 */
import type { AppsResponse, MenuDecl, MenuItemDecl, PublishedApp } from '@/features/apps/apps-api';
import { type MinTier, menuItemsAt, type Tier } from '@/app/experience/tiers';

export interface PlatformDecl {
  name: string | null;
  slug: string | null;
  entries: MenuItemDecl[];
  adminGroups: MenuDecl[];
  /** R11 #662 #665: the platform Data page's sections */
  dataSections: MenuItemDecl[];
}

/** The platform half of GET /api/apps; empty lists while it loads (nothing is invented meanwhile). */
export function platformOf(apps: AppsResponse | undefined): PlatformDecl {
  const p = apps?.platform;
  return { name: p?.name ?? null, slug: p?.slug ?? null, entries: p?.entries ?? [], adminGroups: p?.adminGroups ?? [], dataSections: p?.dataSections ?? [] };
}

const allowed = (held: Set<string>) => (it: MenuItemDecl) => !it.roles?.length || it.roles.some((r) => held.has(r));

export function platformEntries(entries: readonly MenuItemDecl[], roles: Iterable<string>): MenuItemDecl[] {
  return entries.filter(allowed(new Set(roles)));
}

export function adminGroups(groups: readonly MenuDecl[], roles: Iterable<string>): MenuDecl[] {
  const ok = allowed(new Set(roles));
  return groups.map((g) => ({ ...g, items: g.items.filter(ok) })).filter((g) => g.items.length > 0);
}

/** The Admin group a path belongs to (longest matching page route wins), for the breadcrumb. */
export function adminGroupFor(groups: readonly MenuDecl[], pathname: string): { group: MenuDecl; item: MenuItemDecl } | null {
  let best: { group: MenuDecl; item: MenuItemDecl; len: number } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const r = item.route.split(/[?#]/)[0] ?? '';
      if (r.length > 1 && (pathname === r || pathname.startsWith(`${r}/`)) && r.length > (best?.len ?? 0)) best = { group, item, len: r.length };
    }
  }
  return best ? { group: best.group, item: best.item } : null;
}

/** Every place a user can go from the shell: the platform entries, the current app's menus, the Admin pages. */
export function navigableItems(platform: PlatformDecl, app: Pick<PublishedApp, 'menuBar'> | null, roles: Iterable<string>): MenuItemDecl[] {
  const held = [...roles];
  const seen = new Set<string>();
  const all = [...platformEntries(platform.entries, held), ...(app?.menuBar.flatMap((m) => m.items) ?? []), ...adminGroups(platform.adminGroups, held).flatMap((g) => g.items)];
  return all.filter((it) => (seen.has(it.route) ? false : (seen.add(it.route), true)));
}

/** Paths that belong to the platform, not to an app: no app menu bar there (Data › Library is the platform's). */
export const PLATFORM_PATHS = ['/apps', '/my-requests', '/approvals', '/admin', '/library', '/data'] as const;

export function isPlatformPath(pathname: string): boolean {
  return PLATFORM_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * R11 QA11-P1-10: the app whose chrome a location shows. An app's own pages under /apps/<slug>/… and
 * its screens show its bar; a platform page shows none, unless the app opened it for itself
 * (`?app=<slug>`, e.g. Data › Documents). A screen no app declares is the first app's (today, CIGS).
 */
export function appForLocation<A extends Pick<PublishedApp, 'slug' | 'menuBar'>>(
  apps: readonly A[],
  pathname: string,
  search: string,
  byPath: (apps: readonly A[], pathname: string) => A | null,
): A | null {
  if (pathname.startsWith('/apps/')) return apps.find((a) => pathname.startsWith(`/apps/${a.slug}/`) || pathname === `/apps/${a.slug}`) ?? null;
  const slug = new URLSearchParams(search).get('app');
  if (isPlatformPath(pathname)) return slug ? (apps.find((a) => a.slug === slug) ?? null) : null;
  // R-15 group B (Guide §7.2): a shared screen an app opened for itself (a record from its view) keeps that app's bar
  const opener = slug ? apps.find((a) => a.slug === slug) : undefined;
  return opener ?? byPath(apps, pathname) ?? apps[0] ?? null;
}

/**
 * R-14 T-087: the platform's L0 entries at the user's tier (rows under app `platform`). The L0 Admin link
 * reads its row like any entry; while it is hidden the shell's menus and palette leave the Admin pages
 * out too. The Admin rail itself (an admin view) keeps every page the role allows.
 */
export function platformAt(p: PlatformDecl, tier: Tier, min: MinTier): PlatformDecl {
  const entries = menuItemsAt(p.entries, tier, min);
  const adminShown = !p.entries.some((e) => e.id === 'admin') || entries.some((e) => e.id === 'admin');
  return { ...p, entries, adminGroups: adminShown ? p.adminGroups : [] };
}

/**
 * R-14 T-070 (Guide §7.2): an app's L1 item that opens a platform page (My requests, the Library)
 * carries `?app=<slug>`, so the page keeps the app's chrome and the app menu never changes on an L1 link.
 */
export function keepInApp(route: string, slug: string): string {
  const h = route.indexOf('#');
  const hash = h < 0 ? '' : route.slice(h);
  const beforeHash = h < 0 ? route : route.slice(0, h);
  const [path = '', query = ''] = beforeHash.split('?');
  if (!isPlatformPath(path) || new URLSearchParams(query).has('app')) return route;
  return `${path}?${query ? `${query}&` : ''}app=${encodeURIComponent(slug)}${hash}`;
}
